import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tooltip, CircularProgress, Chip
} from '@mui/material';
import {
  WbSunny, Cloud, Opacity, Thunderstorm, AcUnit, 
  WaterDrop, Air, Refresh
} from '@mui/icons-material';
import { weatherAPI } from 'services/api';

// OpenWeather icon mapping
const WEATHER_ICONS = {
  '01d': { icon: <WbSunny />, color: '#FFB300' },  // Clear sky day
  '01n': { icon: <WbSunny />, color: '#7986CB' },  // Clear sky night
  '02d': { icon: <Cloud />, color: '#90CAF9' },    // Few clouds
  '02n': { icon: <Cloud />, color: '#5C6BC0' },
  '03d': { icon: <Cloud />, color: '#78909C' },    // Scattered clouds
  '03n': { icon: <Cloud />, color: '#546E7A' },
  '04d': { icon: <Cloud />, color: '#607D8B' },    // Broken clouds
  '04n': { icon: <Cloud />, color: '#455A64' },
  '09d': { icon: <Opacity />, color: '#42A5F5' },  // Shower rain
  '09n': { icon: <Opacity />, color: '#1E88E5' },
  '10d': { icon: <Opacity />, color: '#29B6F6' },  // Rain
  '10n': { icon: <Opacity />, color: '#0288D1' },
  '11d': { icon: <Thunderstorm />, color: '#5C6BC0' },  // Thunderstorm
  '11n': { icon: <Thunderstorm />, color: '#3949AB' },
  '13d': { icon: <AcUnit />, color: '#B3E5FC' },   // Snow
  '13n': { icon: <AcUnit />, color: '#81D4FA' },
  '50d': { icon: <Air />, color: '#90A4AE' },      // Mist
  '50n': { icon: <Air />, color: '#78909C' },
};

const WeatherWidget = () => {
  const [loading, setLoading] = useState(true);
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState(false);

  const loadWeather = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const response = await weatherAPI.getWidget();
      setWeather(response.data);
    } catch (err) {
      console.error('Weather widget error:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadWeather();
    
    // Refresh every 15 minutes
    const interval = setInterval(loadWeather, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadWeather]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1 }}>
        <CircularProgress size={16} sx={{ color: 'white' }} />
      </Box>
    );
  }

  if (error || !weather || !weather.available) {
    // Show placeholder widget when not configured
    return (
      <Tooltip title="Weather not configured. Admin needs to set API key.">
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.1)',
            cursor: 'help'
          }}
        >
          <Cloud sx={{ fontSize: 20, color: 'rgba(255,255,255,0.5)' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
            --°
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const iconData = WEATHER_ICONS[weather.weather_icon] || WEATHER_ICONS['01d'];
  const temp = weather.temperature !== null ? Math.round(weather.temperature) : '--';
  const rainProb = weather.rain_probability !== null ? Math.round(weather.rain_probability) : null;

  return (
    <Tooltip 
      title={
        <Box sx={{ p: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {weather.farm_name || 'Farm Weather'}
          </Typography>
          <Typography variant="caption" display="block">
            {weather.weather_main || 'Weather'} • {temp}°C
          </Typography>
          {weather.humidity && (
            <Typography variant="caption" display="block">
              Humidity: {weather.humidity}%
            </Typography>
          )}
          {rainProb !== null && rainProb > 0 && (
            <Typography variant="caption" display="block" sx={{ color: rainProb > 50 ? '#FFB74D' : 'inherit' }}>
              Rain: {rainProb}% chance
            </Typography>
          )}
          {weather.is_stale && (
            <Typography variant="caption" display="block" sx={{ color: '#FFCC80', mt: 0.5 }}>
              ⚠️ Data may be outdated
            </Typography>
          )}
        </Box>
      }
      arrow
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 0.5,
          px: 1.5,
          py: 0.5,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.1)',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.2)'
          }
        }}
        onClick={loadWeather}
      >
        {/* Weather Icon */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          color: iconData.color
        }}>
          {React.cloneElement(iconData.icon, { sx: { fontSize: 20 } })}
        </Box>
        
        {/* Temperature */}
        <Typography 
          variant="body2" 
          sx={{ 
            color: 'white', 
            fontWeight: 600,
            fontSize: '0.85rem'
          }}
        >
          {temp}°
        </Typography>
        
        {/* Rain indicator - always show */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          ml: 0.5 
        }}>
          <WaterDrop sx={{ 
            fontSize: 14, 
            color: rainProb > 50 ? '#FFB74D' : rainProb > 20 ? '#90CAF9' : 'rgba(255,255,255,0.6)' 
          }} />
          <Typography 
            variant="caption" 
            sx={{ 
              color: rainProb > 50 ? '#FFB74D' : rainProb > 20 ? '#90CAF9' : 'rgba(255,255,255,0.6)',
              fontWeight: 500,
              fontSize: '0.7rem'
            }}
          >
            {rainProb !== null ? rainProb : 0}%
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

export default WeatherWidget;

